"use client";

import { useState } from "react";
import { Check, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import type { Agent } from "@/lib/api";

function FieldHealth({ value, max }: { value: string; max: number }) {
  const len = value.length;
  const ok = len > 0 && len <= max;
  return (
    <div className="agent-conv-field-foot">
      {ok ? (
        <span className="agent-conv-looks-good">
          <Check size={13} strokeWidth={2.5} aria-hidden />
          Looks good
        </span>
      ) : len > max ? (
        <span className="agent-conv-looks-bad">Over limit</span>
      ) : (
        <span className="agent-conv-looks-muted">Optional</span>
      )}
      <span className={`agent-conv-count${len > max ? " is-over" : ""}`}>
        {len.toLocaleString()} / {max.toLocaleString()}
      </span>
    </div>
  );
}

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
  /** Direct: commentary gate, not AUX-before-TTS. */
  variant?: "cascade" | "direct";
};

export function AgentEditGuardsPanel({ draft, onChange, variant = "cascade" }: Props) {
  const [tipsOpen, setTipsOpen] = useState(false);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  return (
    <div className="agent-conv agent-guards">
      <section className="agent-conv-block">
        <div className="agent-guards-head">
          <h3 className="agent-conv-h">Guards</h3>
          <p className="agent-conv-guards-lead">
            {variant === "direct"
              ? "Guard rules go to the Pulse brain. After the brain drafts a reply, a YES/NO check of that draft against Guard rules runs. NO replaces commentary with the Fixed redirect line (spoken). Adaptive guidance is never spoken. There is no interrupt of live mouth audio."
              : "Policy rules evaluated by the AUX classifier before the normal reply is spoken."}
          </p>
        </div>

        {variant !== "direct" && (
          <div className="agent-conv-switch-row">
            <span className="agent-conv-switch-copy">
              <b>Guardrail interrupt</b>
              <span>On violation, AUX cancels the normal reply and speaks a redirect.</span>
            </span>
            <Switch
              checked={draft.guardrailEnabled === true}
              onCheckedChange={(v) => patch({ guardrailEnabled: v })}
            />
          </div>
        )}

        {(variant === "direct" || draft.guardrailEnabled === true) && (
          <div className="agent-conv-guards">
            {variant !== "direct" && (
              <div className="agent-conv-redirect-cards" role="radiogroup" aria-label="Redirect style">
                <button
                  type="button"
                  role="radio"
                  aria-checked={draft.guardrailRedirectStyle !== "adaptive"}
                  className={draft.guardrailRedirectStyle !== "adaptive" ? "is-active" : undefined}
                  onClick={() => patch({ guardrailRedirectStyle: "fixed" })}
                >
                  <i />
                  <span>
                    <b>Fixed line</b>
                    <em>Always use the same redirect line</em>
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={draft.guardrailRedirectStyle === "adaptive"}
                  className={draft.guardrailRedirectStyle === "adaptive" ? "is-active" : undefined}
                  onClick={() => patch({ guardrailRedirectStyle: "adaptive" })}
                >
                  <i />
                  <span>
                    <b>Adaptive line</b>
                    <em>Generate a context-aware redirect</em>
                  </span>
                </button>
              </div>
            )}

            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>Guard rules</b>
                  <span>Describe what the agent should block or redirect, with examples.</span>
                </div>
                <button
                  type="button"
                  className="agent-guards-tips-link"
                  onClick={() => setTipsOpen((o) => !o)}
                >
                  View writing tips
                  <ChevronRight size={14} aria-hidden />
                </button>
              </div>
              {tipsOpen && (
                <div className="agent-guards-tips" role="note">
                  {variant === "direct"
                    ? "Write concrete policies with short examples. For education agents, allow their domain education; refuse only personal care decisions. On NO, the mouth speaks the Fixed redirect line — never Adaptive guidance."
                    : "Write concrete policies with short examples. Prefer “Do not X; instead Y” over vague tone guidance. Keep rules speakable — the redirect is heard aloud."}
                </div>
              )}
              <textarea
                className="agent-conv-textarea agent-conv-textarea--mid"
                value={draft.guardsPrompt || ""}
                onChange={(e) => patch({ guardsPrompt: e.target.value })}
                placeholder="Do not provide medical diagnoses…"
              />
              <FieldHealth value={draft.guardsPrompt || ""} max={1000} />
            </label>

            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>Fixed redirect line</b>
                  <span>
                    {variant === "direct"
                      ? "Spoken when the Client draft is refused. Must be patient-facing words — never model instructions."
                      : "Always used for Fixed and as fallback if Adaptive fails."}
                  </span>
                </div>
              </div>
              <textarea
                className="agent-conv-textarea agent-conv-textarea--sm"
                value={draft.guardrailRedirectText || ""}
                onChange={(e) => patch({ guardrailRedirectText: e.target.value })}
                placeholder="I can't help with that. What else can I help you with?"
              />
              <FieldHealth value={draft.guardrailRedirectText || ""} max={300} />
            </label>

            {variant !== "direct" && draft.guardrailRedirectStyle === "adaptive" && (
              <label className="agent-conv-field">
                <div className="agent-conv-field-head">
                  <div>
                    <b>Adaptive guidance</b>
                    <span>How the agent should generate a context-aware redirect when a guard is triggered.</span>
                  </div>
                </div>
                <textarea
                  className="agent-conv-textarea agent-conv-textarea--mid"
                  value={draft.guardrailRedirectGuidance || ""}
                  onChange={(e) => patch({ guardrailRedirectGuidance: e.target.value })}
                  placeholder="Refuse briefly and warmly…"
                />
                <FieldHealth value={draft.guardrailRedirectGuidance || ""} max={500} />
              </label>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
